import React, { FC, useEffect, useRef, useState } from 'react';
// style
import './index.scss';

interface SearchFieldProps {
  handleSearch: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

const SEARCH_DEBOUNCE_MS = 1000;

// Debounced patient search; keeps query locally so parent re-renders do not steal focus.
const SearchField: FC<SearchFieldProps> = ({ handleSearch, disabled = false, loading = false }: SearchFieldProps) => {
  const [query, setQuery] = useState('');
  const [isDebouncing, setIsDebouncing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldRestoreFocusRef = useRef(false);

  const isBusy = loading || isDebouncing;

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Restore focus after loading/busy UI updates (list refresh must not blur the field).
  useEffect(() => {
    if (!shouldRestoreFocusRef.current || !inputRef.current) {
      return;
    }

    const input = inputRef.current;
    const end = input.value.length;

    requestAnimationFrame(() => {
      if (!shouldRestoreFocusRef.current || !inputRef.current) {
        return;
      }
      inputRef.current.focus({ preventScroll: true });
      inputRef.current.setSelectionRange(end, end);
    });
  }, [loading, isDebouncing]);

  // Debounces API search; clears immediately when the query is empty.
  const scheduleSearch = (value: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (value.length === 0) {
      setIsDebouncing(false);
      handleSearch('');
      return;
    }

    setIsDebouncing(true);

    timerRef.current = setTimeout(() => {
      setIsDebouncing(false);
      handleSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  // Updates local query and schedules debounced search without lifting state to the parent.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    shouldRestoreFocusRef.current = true;
    setQuery(value);
    scheduleSearch(value);
  };

  // Tracks whether focus should be restored after async list refresh.
  const handleFocus = () => {
    shouldRestoreFocusRef.current = true;
  };

  // Stops restoring focus when the user leaves the field intentionally.
  const handleBlur = () => {
    shouldRestoreFocusRef.current = false;
  };

  return (
    <div className={`search-field${isBusy ? ' search-field_busy' : ''}`}>
      <span className="search-field__icon" aria-hidden="true" />
      <input
        ref={inputRef}
        className="search-field__input"
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="Search by patient"
        disabled={disabled}
        aria-busy={isBusy}
      />
      <span
        className={`search-field__spinner btn__spinner${isBusy ? ' search-field__spinner_visible' : ''}`}
        aria-hidden="true"
      />
    </div>
  );
};

export default SearchField;
